import { daysBetween, isIsoDate } from '../format/dates'
import { MAX_MOVEMENT_QTY } from './stockMovements'
import { stockLocations, type PurchaseLineInput, type StockCountLine, type StockItem, type StockLocation } from '../types/stock'

/**
 * Rekenregels voor de wekelijkse telling en de inkoopfactuur (docs/VOORRAAD.md). Puur en getest;
 * de server rekent hetzelfde.
 */

/** Elke plek wordt minstens eens per zoveel dagen helemaal geteld. */
export const COUNT_INTERVAL_DAYS = 7

/** Is de telling van een plek aan de beurt? `lastAt` = laatste volledige telling ("YYYY-MM-DD…"), of `null`. */
export function countDue(lastAt: string | null, today: string): { due: boolean; daysSince: number | null } {
  if (!lastAt) return { due: true, daysSince: null }
  const daysSince = daysBetween(lastAt.slice(0, 10), today)
  return { due: daysSince >= COUNT_INTERVAL_DAYS, daysSince }
}

/** Wat er op een plek geteld moet worden: alles wat daar bijgehouden wordt. */
export const countableAt = (items: readonly StockItem[], location: StockLocation) => items.filter((i) => i.levels[location] !== null)

/** Verschil per getelde regel, en samen. `cost` = inkoopprijs per artikel (onbekend = 0). */
export function countSummary(lines: readonly StockCountLine[], cost: (itemId: string) => number | undefined) {
  let differences = 0
  let netQty = 0
  let netCostCents = 0
  lines.forEach((l) => {
    const diff = l.counted - (l.expected ?? 0)
    if (diff !== 0) differences += 1
    netQty += diff
    netCostCents += diff * (cost(l.itemId) ?? 0)
  })
  return { differences, netQty, netCostCents }
}

/**
 * Plekken waar de wekelijkse telling aan de beurt is. Een plek waar niets bijgehouden wordt, telt
 * niet mee: daar valt niets te tellen. Eén regel voor het overzicht en het tabblad Tellen.
 */
export function locationsDue(
  items: readonly StockItem[],
  lastCountAt: Readonly<Record<StockLocation, string | null>>,
  today: string,
): StockLocation[] {
  return stockLocations.filter((l) => countableAt(items, l).length > 0 && countDue(lastCountAt[l], today).due)
}

/** Een geldig geteld aantal: een heel getal, 0 of meer. */
export const validCount = (n: number) => Number.isInteger(n) && n >= 0 && n <= 99_999

// ——— Inkoopfactuur ———

export const RECEIPT_FILE_MAX_BYTES = 10 * 1024 * 1024
export const RECEIPT_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const

/** Wat er niet klopt aan de bijlage, of `null`. */
export function receiptFileProblem(file: { size: number; type: string }): string | null {
  if (!(RECEIPT_FILE_TYPES as readonly string[]).includes(file.type)) return 'Kies een PDF of een foto (JPG, PNG of WebP).'
  if (file.size > RECEIPT_FILE_MAX_BYTES) return 'Het bestand is groter dan 10 MB. Maak een kleinere scan of foto.'
  return null
}

/** Som van de regels in centen (excl. btw). Een regel zonder prijs telt als 0. */
export const receiptTotalCents = (lines: readonly PurchaseLineInput[]) => lines.reduce((s, l) => s + l.qty * (l.costPriceCents ?? 0), 0)

/** Hetzelfde artikel twee keer op de factuur: samenvoegen; de laatst ingevulde prijs telt. */
export function mergeReceiptLines(lines: readonly PurchaseLineInput[]): PurchaseLineInput[] {
  // Per artikel én regel, maar winkel, salongebruik en sample blijven apart (andere inkoopprijs-regel).
  const byId = new Map<string, PurchaseLineInput>()
  lines.forEach((l) => {
    const use = l.use && l.use !== 'winkel' ? l.use : undefined
    const key = use ? `${l.itemId}|${use}` : l.itemId
    const known = byId.get(key)
    const costPriceCents = l.costPriceCents ?? known?.costPriceCents
    byId.set(key, { itemId: l.itemId, qty: (known?.qty ?? 0) + l.qty, ...(costPriceCents !== undefined ? { costPriceCents } : {}), ...(use ? { use } : {}) })
  })
  return [...byId.values()]
}

export type ReceiptFields = { supplier: string; invoiceNumber: string; invoiceDate: string; lines: readonly PurchaseLineInput[] }

/** Per veld wat er niet klopt; leeg = boeken kan. `lines.N` = een regel. */
export function receiptProblems(r: ReceiptFields, today: string): Record<string, string> {
  const problems: Record<string, string> = {}
  if (!r.supplier.trim()) problems.supplier = 'Vul de leverancier in.'
  if (!r.invoiceNumber.trim()) problems.invoiceNumber = 'Vul het factuurnummer in.'
  if (!isIsoDate(r.invoiceDate)) problems.invoiceDate = 'Vul de factuurdatum in.'
  else if (r.invoiceDate > today) problems.invoiceDate = 'De factuurdatum ligt in de toekomst.'
  if (r.lines.length === 0) problems.lines = 'Zet minstens één artikel op de factuur.'
  r.lines.forEach((l, i) => {
    if (!l.itemId) problems[`lines.${i}`] = 'Kies een artikel.'
    else if (!Number.isInteger(l.qty) || l.qty <= 0 || l.qty > MAX_MOVEMENT_QTY) problems[`lines.${i}`] = 'Vul een aantal in.'
    else if (l.costPriceCents !== undefined && (!Number.isInteger(l.costPriceCents) || l.costPriceCents < 0)) problems[`lines.${i}`] = 'Vul een geldige prijs in.'
  })
  return problems
}

/** Factuurbedrag tegen de regels: binnen 1 cent per regel (afronding) geldt als "klopt". */
export function receiptMismatchCents(lines: readonly PurchaseLineInput[], invoiceTotalCents: number | undefined): number | null {
  if (invoiceTotalCents === undefined) return null
  const diff = invoiceTotalCents - receiptTotalCents(lines)
  return Math.abs(diff) <= lines.length ? 0 : diff
}
